import React, { useState, useEffect } from 'react';
import { sitesAPI } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

import { Loader2, Trash2, MapPin, Plus } from 'lucide-react';
import { Separator } from './ui/separator';

export default function SiteManager({ projectId, isOpen, onClose, onSiteCreated }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    country: '',
    description: '',
  });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isOpen && projectId) {
      loadSites();
    }
  }, [isOpen, projectId]);

  const loadSites = async () => {
    try {
      setLoading(true);
      const result = await sitesAPI.getAll(projectId);
      if (result.success) {
        setSites(result.sites || []);
      }
    } catch (error) {
      console.error('Error loading sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsCreating(true);

    try {
      const result = await sitesAPI.create({
        ...formData,
        project_id: projectId,
      });

      if (result.success) {
        setFormData({ name: '', city: '', country: '', description: '' });
        await loadSites();
        if (onSiteCreated) {
          onSiteCreated(result.site);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to create site');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm('Are you sure you want to delete this site?')) {
      return;
    }

    try {
      await sitesAPI.delete(siteId);
      await loadSites();
    } catch (error) {
      setError(error.message || 'Failed to delete site');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Sites</DialogTitle>
          <DialogDescription>
            Create and manage sites for your project.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-6">
          {/* Create Form */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium leading-none flex items-center gap-2">
              <Plus className="h-4 w-4" /> Add New Site
            </h3>

            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="site-name">Site Name <span className="text-destructive">*</span></Label>
                <Input
                  id="site-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Main Office"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="site-city">City</Label>
                  <Input
                    id="site-city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="New York"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="site-country">Country</Label>
                  <Input
                    id="site-country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="USA"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="site-description">Description</Label>
                <Textarea
                  id="site-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows={2}
                />
              </div>

              <Button type="submit" className="w-full" disabled={isCreating}>
                {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isCreating ? 'Creating...' : 'Create Site'}
              </Button>
            </form>
          </div>

          <Separator />

          {/* Sites List */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <h3 className="text-sm font-medium leading-none mb-3">Existing Sites</h3>
            <div className="flex-1 -mx-2 px-2 overflow-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </div>
              ) : sites.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg border-dashed">
                  No sites created yet
                </div>
              ) : (
                <div className="space-y-2 pb-4">
                  {sites.map((site) => (
                    <div
                      key={site.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/10 transition-colors"
                    >
                      <div className="space-y-1 overflow-hidden">
                        <p className="text-sm font-medium leading-none truncate">{site.name}</p>
                        {(site.city || site.country) && (
                          <div className="flex items-center text-xs text-muted-foreground truncate">
                            <MapPin className="h-3 w-3 mr-1 shrink-0" />
                            {[site.city, site.country].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => handleDelete(site.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
